/**
 * Departments and teams.
 *
 * One page for both, because they are one idea: the axes a data scope is
 * measured against. A role granted "at a department" resolves through these
 * rows, which is why deleting a department that still has people in it is
 * refused — the scope would silently stop matching anything.
 *
 * Both lists are short and hand-managed, so they are fetched whole rather than
 * paged. A register with a pager under six rows is a register pretending.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, LayoutGrid, Pencil, Plus, Trash2, Users } from 'lucide-react';
import {
  ApiError,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  Confirm,
  EmptyState,
  PageHeader,
  QueryPending,
  StatusChip,
  Tabs,
  api,
  toast,
} from '@connect/web-shared';

import { OrgUnitDialog, type OrgUnit } from './org-unit-dialog';

interface DepartmentRow extends OrgUnit {
  parent_name: string | null;
  headcount: number;
  is_active: boolean;
}

interface TeamRow extends OrgUnit {
  department_name: string;
  headcount: number;
  is_active: boolean;
}

export function OrgUnitsPage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'departments' | 'teams'>('departments');
  const [editing, setEditing] = useState<OrgUnit | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [removing, setRemoving] = useState<{ kind: 'department' | 'team'; row: OrgUnit } | null>(
    null,
  );

  const departments = useQuery({
    queryKey: ['hr', 'departments'],
    queryFn: async () => {
      const page = await api.page<DepartmentRow>('/hr/departments', {
        params: { page_size: 200, sort: 'name:asc' },
      });
      return page.rows;
    },
  });

  const teams = useQuery({
    queryKey: ['hr', 'teams'],
    queryFn: async () => {
      const page = await api.page<TeamRow>('/hr/teams', {
        params: { page_size: 200, sort: 'name:asc' },
      });
      return page.rows;
    },
  });

  const remove = useMutation({
    mutationFn: ({ kind, row }: { kind: 'department' | 'team'; row: OrgUnit }) =>
      api.delete(`/hr/${kind === 'department' ? 'departments' : 'teams'}/${row.public_id}`),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['hr'] });
      toast.success(`${variables.row.name} removed`);
      setRemoving(null);
    },
    onError: (error) => {
      // "4 people are in Delivery" names what is in the way, so it is shown
      // as written rather than replaced with a generic failure.
      toast.error(
        'Could not remove it',
        error instanceof ApiError ? error.message : undefined,
      );
    },
  });

  const open = (unit: OrgUnit | null) => {
    setEditing(unit);
    setDialogOpen(true);
  };

  const isDepartments = tab === 'departments';
  const shown = isDepartments ? departments : teams;
  const rows: (DepartmentRow | TeamRow)[] =
    (isDepartments ? departments.data : teams.data) ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Departments and teams"
        description="The shape of the organization, and the axes every data scope is measured against."
        actions={
          <Can permission={isDepartments ? 'hr.department.create' : 'hr.team.create'}>
            <Button size="sm" onClick={() => open(null)}>
              <Plus className="size-3.5" />
              {isDepartments ? 'New department' : 'New team'}
            </Button>
          </Can>
        }
        tabs={
          <Tabs
            value={tab}
            onChange={(id) => setTab(id as 'departments' | 'teams')}
            items={[
              { id: 'departments', label: 'Departments', count: departments.data?.length },
              { id: 'teams', label: 'Teams', count: teams.data?.length },
            ]}
          />
        }
      />

      <Card>
        <CardHeader
          title={isDepartments ? 'Departments' : 'Teams'}
          description={
            isDepartments
              ? 'Nest them to describe divisions. A scoped role reaches a department and everything under it.'
              : 'A team sits inside a department. A team-scoped role reaches only its own.'
          }
        />
        <CardBody className="p-0">
          {shown.data === undefined ? (
            <QueryPending query={shown} label="Loading" />
          ) : rows.length === 0 ? (
            <EmptyState
              title={isDepartments ? 'No departments' : 'No teams'}
              description={
                isDepartments
                  ? 'Until there is one, every role is organization-wide — which may be exactly right for a small installation.'
                  : 'Teams are optional. Add one when a department is big enough that a lead needs their own slice.'
              }
              action={
                <Can permission={isDepartments ? 'hr.department.create' : 'hr.team.create'}>
                  <Button size="sm" onClick={() => open(null)}>
                    <Plus className="size-3.5" />
                    Add one
                  </Button>
                </Can>
              }
            />
          ) : (
            <ul className="divide-y divide-line">
              {rows.map((row) => (
                <li key={row.public_id} className="flex items-center gap-3 px-4 py-2.5">
                  {isDepartments ? (
                    <LayoutGrid className="size-4 shrink-0 text-faint" aria-hidden />
                  ) : (
                    <Users className="size-4 shrink-0 text-faint" aria-hidden />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-body">
                      {row.name}
                    </span>
                    <span className="flex items-center gap-1 text-2xs text-faint">
                      {row.code ? <span className="font-mono">{row.code}</span> : null}
                      {'parent_name' in row && row.parent_name ? (
                        <span>· inside {row.parent_name}</span>
                      ) : null}
                      {'department_name' in row ? (
                        <span className="flex items-center gap-0.5">
                          <Building2 className="size-2.5" />
                          {row.department_name}
                        </span>
                      ) : null}
                    </span>
                  </span>
                  <span className="shrink-0 text-xs text-muted tabular">
                    {row.headcount} {row.headcount === 1 ? 'person' : 'people'}
                  </span>
                  {!row.is_active ? (
                    <StatusChip value="inactive" tones={{ inactive: 'neutral' }} />
                  ) : null}
                  <Can permission={isDepartments ? 'hr.department.edit' : 'hr.team.edit'}>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Edit ${row.name}`}
                      onClick={() => open(row)}
                    >
                      <Pencil className="size-3.5" />
                    </Button>
                  </Can>
                  <Can permission={isDepartments ? 'hr.department.delete' : 'hr.team.delete'}>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Remove ${row.name}`}
                      onClick={() =>
                        setRemoving({ kind: isDepartments ? 'department' : 'team', row })
                      }
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </Can>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <OrgUnitDialog
        open={dialogOpen}
        kind={isDepartments ? 'department' : 'team'}
        unit={editing}
        departments={departments.data ?? []}
        onClose={() => setDialogOpen(false)}
        onSaved={() => {
          void queryClient.invalidateQueries({ queryKey: ['hr'] });
          setDialogOpen(false);
        }}
      />

      <Confirm
        open={removing !== null}
        title={`Remove ${removing?.row.name ?? ''}?`}
        message={
          removing?.kind === 'department'
            ? 'Refused if anybody is still in it or another department sits under it — a scope pointing at a department that no longer exists silently stops matching rows.'
            : 'Refused if a role is still scoped to this team.'
        }
        confirmLabel="Remove"
        onClose={() => setRemoving(null)}
        onConfirm={async () => {
          if (removing) await remove.mutateAsync(removing);
        }}
      />
    </div>
  );
}
